import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './Suppliers.css';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';

const SuppliersList = forwardRef(({ onSelectSupplier, selectedSupplierId, onNew }, ref) => {
  const { t } = useTranslation('procurement');
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Active by default; inactive suppliers can be listed to reactivate them (#361)
  const [statusFilter, setStatusFilter] = useState('active');

  useImperativeHandle(ref, () => ({
    fetchSuppliers
  }));

  useEffect(() => {
    fetchSuppliers();
  }, [statusFilter]);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page_size: '500' });
      if (statusFilter !== 'all') params.set('is_active', statusFilter === 'active' ? 'true' : 'false');
      const response = await apiFetch(getApiUrl(`/api/providers?${params}`));
      if (response.ok) {
        const data = await response.json();
        // API returns {providers: [...]}
        setSuppliers(data.providers || []);
        setError(null);
      } else {
        setError(t('suppliers.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setError(t('suppliers.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredSuppliers = suppliers.filter(supplier => (
    supplier.provider_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.provider_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.email?.toLowerCase().includes(searchTerm.toLowerCase())
  ));

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && suppliers.length === 0) {
    return <div className="list-container"><div className="loading">{t('suppliers.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('suppliers.title')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('suppliers.newSupplier')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('suppliers.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
            aria-label={t('suppliers.filters.statusLabel')}
          >
            <option value="active">{t('suppliers.filters.active')}</option>
            <option value="inactive">{t('suppliers.filters.inactive')}</option>
            <option value="all">{t('suppliers.filters.allStatuses')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('suppliers.tableHeaders.code')}</th>
              <th>{t('suppliers.tableHeaders.name')}</th>
              <th>{t('suppliers.tableHeaders.location')}</th>
              <th>{t('suppliers.tableHeaders.contact')}</th>
              <th>{t('suppliers.tableHeaders.paymentTerms')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredSuppliers.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-data">
                  {searchTerm ? t('suppliers.noData.searching') : t('suppliers.noData.empty')}
                </td>
              </tr>
            ) : (
              filteredSuppliers.map(supplier => (
                <tr
                  key={supplier.provider_id}
                  onClick={() => onSelectSupplier(supplier.provider_id)}
                  className={selectedSupplierId === supplier.provider_id ? 'selected' : ''}
                >
                  <td className="supplier-code">
                    <code>{supplier.provider_code || t('suppliers.defaults.notApplicable')}</code>
                  </td>
                  <td className="supplier-info">
                    <div className="supplier-name">
                      <strong>{supplier.provider_name || t('suppliers.defaults.unknown')}</strong>
                      {!supplier.is_active && (
                        <span className="status-badge status-inactive" style={{ marginLeft: '0.5rem' }}>
                          {t('suppliers.status.inactive')}
                        </span>
                      )}
                    </div>
                    {supplier.contact_person && (
                      <div className="supplier-contact">{supplier.contact_person}</div>
                    )}
                  </td>
                  <td>
                    {[supplier.city, supplier.state].filter(Boolean).join(', ')}
                  </td>
                  <td className="supplier-contact">
                    {supplier.email && (
                      <div>{supplier.email}</div>
                    )}
                    {supplier.phone && (
                      <div className="supplier-type">{supplier.phone}</div>
                    )}
                    {!supplier.email && !supplier.phone && (
                      <div className="supplier-type">{t('suppliers.defaults.noContact')}</div>
                    )}
                  </td>
                  <td className="supplier-type">
                    {supplier.payment_terms || t('suppliers.defaults.net30')}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="list-footer">
        <div className="record-count">
          {t('suppliers.recordCount', { filtered: filteredSuppliers.length, total: suppliers.length })}
        </div>
      </div>
    </div>
  );
});

SuppliersList.displayName = 'SuppliersList';

export default SuppliersList;